import { useLocalAudioUpload } from "@/lib/hooks/slides/editor/useLocalAudioUpload";
import { useLocalVideoUpload } from "@/lib/hooks/slides/editor/useLocalVideoUpload";
import { useAppDispatch } from "@/store/hooks";
import { updateElementProps } from "@/store/slideEditorSlice";
import { CANVAS_DEFAULT_FONT_FAMILY, answerColors } from "@ai-teach/canvas-styles";
import type { OrderAnswer, OrderTextElement } from "@ai-teach/types";
import { IconButton } from "@ai-teach/ui-web";
import { useRef, useState } from "react";
import { FaMusic, FaPause, FaPlay } from "react-icons/fa";
import { HiPlus, HiX } from "react-icons/hi";
import { MdArrowDownward, MdArrowUpward, MdVideoLibrary } from "react-icons/md";

interface OrderTextRendererProps {
  element: OrderTextElement;
  isLiveMode?: boolean;
  answer?: OrderAnswer | undefined;
  isFrozen?: boolean | undefined;
}

function generateId() {
  return Math.random().toString(36).slice(2, 10);
}

const nullMedia = { src: null, id: null, filename: null };

export function OrderTextRenderer({
  element,
  isLiveMode = false,
  answer,
  isFrozen,
}: OrderTextRendererProps) {
  const dispatch = useAppDispatch();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Live-preview-only typing state, since web has no real answer-submission plumbing for this element (StaticElement is read-only replay).
  const [previewTyped, setPreviewTyped] = useState<Record<number, string>>({});
  const [previewResults, setPreviewResults] = useState<Record<number, boolean | null>>({});

  const handlePreviewBlur = (index: number) => {
    if (previewResults[index] !== undefined && previewResults[index] !== null) return;
    const typed = previewTyped[index]?.trim() ?? "";
    if (!typed) return;
    const correctItem = element.items[index];
    const isCorrect = !!correctItem && typed.toLowerCase() === correctItem.text.toLowerCase();
    setPreviewResults((prev) => ({ ...prev, [index]: isCorrect }));
  };

  const { handleFileDrop: handleAudioDrop, isUploading: isUploadingAudio } = useLocalAudioUpload(
    (media) =>
      dispatch(updateElementProps({ id: element.id, props: { media, mediaType: "audio" } }))
  );
  const { handleFileDrop: handleVideoDrop, isUploading: isUploadingVideo } = useLocalVideoUpload(
    (media) =>
      dispatch(updateElementProps({ id: element.id, props: { media, mediaType: "video" } }))
  );

  /** What the student put at one position, or null when this isn't a graded replay. */
  const gradeSlot = (index: number) => {
    if (!isFrozen || !answer) return null;
    const item = element.items[index];
    const studentText =
      answer.typedTexts?.[`${index}`] ??
      element.items.find((it) => it.id === answer.order[index])?.text ??
      "";
    return { studentText, isCorrect: !!item && answer.order[index] === item.id };
  };

  const updateItems = (items: OrderTextElement["items"]) => {
    dispatch(updateElementProps({ id: element.id, props: { items } }));
  };

  const setMediaType = (type: "audio" | "video" | null) => {
    dispatch(updateElementProps({ id: element.id, props: { mediaType: type, media: nullMedia } }));
    setIsPlaying(false);
  };

  const handleRemoveMedia = () => {
    dispatch(updateElementProps({ id: element.id, props: { mediaType: null, media: nullMedia } }));
    setIsPlaying(false);
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleAddItem = () => {
    updateItems([...element.items, { id: generateId(), text: "" }]);
  };

  const handleRemoveItem = (index: number) => {
    if (element.items.length <= 2) return;
    updateItems(element.items.filter((_, i) => i !== index));
  };

  const handleTextChange = (index: number, text: string) => {
    updateItems(element.items.map((it, i) => (i === index ? { ...it, text } : it)));
  };

  const handleMoveItem = (index: number, direction: "up" | "down") => {
    const target = direction === "up" ? index - 1 : index + 1;
    if (target < 0 || target >= element.items.length) return;
    const next = [...element.items];
    const a = next[index];
    const b = next[target];
    if (!a || !b) return;
    next[index] = b;
    next[target] = a;
    updateItems(next);
  };

  const { mediaType, media } = element;
  const itemFontSize = element.fontSize ?? 20;

  return (
    <div
      className="w-full h-full flex flex-col gap-3 p-2 overflow-y-auto text-black"
      style={{
        borderRadius: element.border.cornerRadius,
        backgroundColor: element.backgroundColor ?? "transparent",
        pointerEvents: isLiveMode ? "none" : "auto",
      }}
    >
      {/* Media section (editor only) */}
      {!isLiveMode && (
        <div className="shrink-0 rounded-lg border border-gray-200 overflow-hidden">
          <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 border-b border-gray-200">
            <span className="text-[9px] font-semibold text-gray-400 uppercase tracking-widest flex-1">
              Question Media
            </span>
            {(["audio", "video"] as const).map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => setMediaType(mediaType === type ? null : type)}
                className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-semibold transition-colors ${
                  mediaType === type
                    ? "bg-violet-600 text-white"
                    : "bg-white border border-gray-200 text-gray-400 hover:text-gray-700"
                }`}
              >
                {type === "audio" ? (
                  <FaMusic className="text-[8px]" />
                ) : (
                  <MdVideoLibrary className="text-[9px]" />
                )}
                {type === "audio" ? "Audio" : "Video"}
              </button>
            ))}
          </div>

          {mediaType === "audio" &&
            (isUploadingAudio ? (
              <div className="flex items-center justify-center gap-1.5 py-2">
                <div className="w-3 h-3 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
                <span className="text-[10px] text-gray-400">Uploading...</span>
              </div>
            ) : media?.src ? (
              <div className="flex items-center gap-2 px-2 py-1.5">
                <audio ref={audioRef} src={media.src} onEnded={() => setIsPlaying(false)}>
                  <track kind="captions" srcLang="en" default />
                </audio>
                <button
                  type="button"
                  onClick={togglePlay}
                  className="w-6 h-6 rounded-full bg-violet-500 flex items-center justify-center shrink-0"
                >
                  {isPlaying ? (
                    <FaPause className="text-white text-[8px]" />
                  ) : (
                    <FaPlay className="text-white text-[8px] ml-0.5" />
                  )}
                </button>
                <span className="flex-1 text-[10px] text-gray-500 truncate">{media.filename}</span>
                <IconButton type="button" variant="danger" size="sm" onClick={handleRemoveMedia}>
                  <HiX className="text-xs" />
                </IconButton>
              </div>
            ) : (
              <label className="flex items-center justify-center gap-1.5 py-2 cursor-pointer hover:bg-gray-50">
                <input
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleAudioDrop(f);
                    e.target.value = "";
                  }}
                />
                <FaMusic className="text-gray-300 text-xs" />
                <span className="text-[10px] text-gray-400">Upload audio</span>
              </label>
            ))}

          {mediaType === "video" &&
            (isUploadingVideo ? (
              <div className="flex items-center justify-center gap-1.5 py-2">
                <div className="w-3 h-3 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
                <span className="text-[10px] text-gray-400">Uploading...</span>
              </div>
            ) : media?.src ? (
              <div className="relative">
                <video src={media.src} controls className="w-full max-h-24 object-contain bg-black">
                  <track kind="captions" srcLang="en" default />
                </video>
                <IconButton
                  type="button"
                  variant="danger"
                  size="sm"
                  onClick={handleRemoveMedia}
                  className="absolute top-1 right-1"
                >
                  <HiX className="text-xs" />
                </IconButton>
              </div>
            ) : (
              <label className="flex items-center justify-center gap-1.5 py-2 cursor-pointer hover:bg-gray-50">
                <input
                  type="file"
                  accept="video/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleVideoDrop(f);
                    e.target.value = "";
                  }}
                />
                <MdVideoLibrary className="text-gray-300 text-sm" />
                <span className="text-[10px] text-gray-400">Upload video</span>
              </label>
            ))}

          {!mediaType && (
            <div className="flex items-center justify-center py-1.5">
              <span className="text-[9px] text-gray-300">Select audio or video above</span>
            </div>
          )}
        </div>
      )}

      {/* Live mode: show media if present */}
      {isLiveMode && mediaType === "audio" && media?.src && (
        <div className="shrink-0 flex items-center gap-2 px-2 py-1.5 bg-violet-50 rounded-lg border border-violet-100">
          <FaMusic className="text-violet-400 text-xs shrink-0" />
          <span
            className="text-[10px] text-violet-600 font-medium truncate"
            style={{ fontFamily: CANVAS_DEFAULT_FONT_FAMILY }}
          >
            {media.filename ?? "Audio"}
          </span>
        </div>
      )}
      {isLiveMode && mediaType === "video" && media?.src && (
        <video
          src={media.src}
          controls
          className="w-full max-h-24 object-contain bg-black rounded-lg shrink-0"
        >
          <track kind="captions" srcLang="en" default />
        </video>
      )}

      {/* Text items */}
      {element.items.map((item, index) => {
        const graded = gradeSlot(index);
        const previewResult = previewResults[index] ?? null;
        const isPreviewGraded = isLiveMode && !graded && previewResult !== null;

        const rowBoxStyle =
          graded || isPreviewGraded
            ? {
                borderColor: (graded ?? { isCorrect: previewResult }).isCorrect
                  ? answerColors.correct
                  : answerColors.wrong,
                backgroundColor: (graded ?? { isCorrect: previewResult }).isCorrect
                  ? answerColors.correctSoft
                  : answerColors.wrongSoft,
              }
            : undefined;
        const badgeStyle =
          graded || isPreviewGraded
            ? {
                backgroundColor: (graded ?? { isCorrect: previewResult }).isCorrect
                  ? answerColors.correct
                  : answerColors.wrong,
              }
            : undefined;

        const badge = (
          <div
            className="shrink-0 w-6 h-6 rounded-full bg-violet-500 text-white text-xs font-bold flex items-center justify-center"
            style={badgeStyle}
          >
            {index + 1}
          </div>
        );

        if (isLiveMode) {
          return (
            <div
              key={item.id}
              className="flex items-center gap-2 bg-white/80 rounded-lg border border-gray-200 px-2 py-1.5"
              style={rowBoxStyle}
            >
              {badge}
              {(() => {
                return graded ? (
                  <>
                    <span
                      className="flex-1 font-medium truncate"
                      style={{
                        fontFamily: CANVAS_DEFAULT_FONT_FAMILY,
                        fontSize: itemFontSize,
                        color: graded.isCorrect ? "#065f46" : "#991b1b",
                      }}
                    >
                      {graded.studentText || "—"}
                    </span>
                    {!graded.isCorrect && (
                      <span
                        className="shrink-0 text-xs font-bold rounded px-1.5 py-0.5"
                        style={{
                          fontFamily: CANVAS_DEFAULT_FONT_FAMILY,
                          color: "#065f46",
                          backgroundColor: answerColors.correctSoft,
                        }}
                      >
                        {item.text}
                      </span>
                    )}
                  </>
                ) : isPreviewGraded ? (
                  <>
                    <span
                      className="flex-1 font-medium truncate"
                      style={{
                        fontFamily: CANVAS_DEFAULT_FONT_FAMILY,
                        fontSize: itemFontSize,
                        color: previewResult ? "#065f46" : "#991b1b",
                      }}
                    >
                      {previewTyped[index]?.trim() || "—"}
                    </span>
                    {!previewResult && (
                      <span
                        className="shrink-0 text-xs font-bold rounded px-1.5 py-0.5"
                        style={{
                          fontFamily: CANVAS_DEFAULT_FONT_FAMILY,
                          color: "#065f46",
                          backgroundColor: answerColors.correctSoft,
                        }}
                      >
                        {item.text}
                      </span>
                    )}
                  </>
                ) : (
                  <div className="relative flex-1 min-w-0">
                    {/* A real placeholder attribute renders through the browser's
                        `::placeholder` pseudo-element, which — unlike normal text (compare the
                        "First"/"Second"/"Third" values above, which render fine) — was clipping
                        the top of capital letters at this scaled-down size. This plain span uses
                        the exact same rendering path as real typed text instead. */}
                    {!previewTyped[index] && (
                      <span
                        className="absolute inset-0 flex items-center text-gray-300 pointer-events-none truncate"
                        style={{ fontFamily: CANVAS_DEFAULT_FONT_FAMILY, fontSize: itemFontSize }}
                      >
                        Type your answer...
                      </span>
                    )}
                    <input
                      type="text"
                      value={previewTyped[index] ?? ""}
                      onChange={(e) =>
                        setPreviewTyped((prev) => ({ ...prev, [index]: e.target.value }))
                      }
                      onBlur={() => handlePreviewBlur(index)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.currentTarget.blur();
                      }}
                      data-no-moveable-drag
                      className="w-full bg-transparent outline-none border-none min-w-0"
                      style={{
                        fontFamily: CANVAS_DEFAULT_FONT_FAMILY,
                        fontSize: itemFontSize,
                        // Overrides the outer container's pointerEvents: "none" (a normally read-only live-mode preview) so this is typable.
                        pointerEvents: "auto",
                        lineHeight: "normal",
                      }}
                    />
                  </div>
                );
              })()}
            </div>
          );
        }

        return (
          <div
            key={item.id}
            className="flex items-center gap-2 bg-white/80 rounded-lg border border-gray-200 px-2 py-1.5"
          >
            {badge}
            <input
              type="text"
              value={item.text}
              onChange={(e) => handleTextChange(index, e.target.value)}
              data-no-moveable-drag
              className="flex-1 bg-transparent outline-none placeholder:text-gray-300 min-w-0"
              placeholder={`Item ${index + 1}...`}
              style={{ fontFamily: CANVAS_DEFAULT_FONT_FAMILY, fontSize: itemFontSize }}
            />

            <div className="flex flex-col gap-0.5 shrink-0">
              <button
                type="button"
                disabled={index === 0}
                onClick={() => handleMoveItem(index, "up")}
                className="w-4 h-4 flex items-center justify-center rounded text-gray-400 hover:text-gray-700 disabled:opacity-20 disabled:cursor-not-allowed"
              >
                <MdArrowUpward className="text-xs" />
              </button>
              <button
                type="button"
                disabled={index === element.items.length - 1}
                onClick={() => handleMoveItem(index, "down")}
                className="w-4 h-4 flex items-center justify-center rounded text-gray-400 hover:text-gray-700 disabled:opacity-20 disabled:cursor-not-allowed"
              >
                <MdArrowDownward className="text-xs" />
              </button>
            </div>
            {element.items.length > 2 && (
              <IconButton
                type="button"
                variant="danger"
                size="sm"
                onClick={() => handleRemoveItem(index)}
              >
                <HiX className="text-xs" />
              </IconButton>
            )}
          </div>
        );
      })}

      {!isLiveMode && (
        <button
          type="button"
          onClick={handleAddItem}
          className="flex items-center justify-center gap-1 py-1.5 rounded-lg border-2 border-dashed border-gray-300 hover:border-violet-400 hover:text-violet-500 text-gray-400 text-[10px] font-semibold transition-colors"
        >
          <HiPlus className="text-xs" />
          Add Item
        </button>
      )}
    </div>
  );
}
